import { useState, useEffect, CSSProperties } from "react";
import { useResizeObserver } from "@react-hookz/web";
import { FragmentOf, ResultOf } from "gql.tada";
import { motion, PanInfo, AnimationOptions, Transition } from "motion/react";
import cx from "classnames";
import { RichEntryFragment } from "@queries/fragments/RichEntryFragment";
import { ThemeModel } from "../../../types/ThemeModel";
import { animationCurves } from "@utils/animationCurves";
import { useIsDesktop } from "@hooks/useIsDesktop";
import { Button } from "@components/shared/core/Button";
import CarouselItem from "./CarouselItem";
import { LinkFragment } from "@queries/fragments/LinkFragment";
import { Link } from "@components/shared/core/Link";

export type Slide = {
  content: string;
  image?: string | null;
  icon: string | null;
  sourceInfo?: FragmentOf<typeof RichEntryFragment>;
  highlightTheme?: ThemeModel | null;
};

type TProps = {
  slides: Slide[];
  slideWidth: number;
  slideHeight: number;
  gap: number;
  accessibilityControlLabelPrevious: string | null;
  accessibilityControlLabelNext: string | null;
  ctaLink?: ResultOf<typeof LinkFragment> | null;
};

const ArrowBtn = ({
  className = "",
  disabled = false,
  ariaLabel,
  style,
  onClick,
}: {
  className?: string;
  disabled?: boolean;
  ariaLabel: string | null;
  style?: CSSProperties;
  onClick: () => void;
}) => {
  return (
    <Button
      disabled={disabled}
      variant="light-on-blur"
      size="inherit"
      className={cx("disabled:opacity-40", className)}
      aria-label={ariaLabel || ""}
      style={style}
      onClick={onClick}
    >
      <span className="flex justify-center items-center">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="17"
          height="16"
          fill="none"
        >
          <path
            d="m8.5 1 7 7-7 7M14.833 8H1.5"
            stroke="#fff"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </Button>
  );
};

const controlsOffset = 150;
const dragSensitivity = 1.5;

const Carousel = ({
  slides,
  slideWidth,
  slideHeight,
  accessibilityControlLabelPrevious,
  accessibilityControlLabelNext,
  gap,
  ctaLink,
}: TProps) => {
  const startSlide = slides.length;
  const totalWidth = slideWidth + gap;
  // Controls current position
  const [offset, setOffset] = useState(startSlide * totalWidth);
  const [direction, setDirection] = useState<1 | -1 | 0>(0);
  const [animationType, setAnimationType] = useState<
    Transition | AnimationOptions
  >(animationCurves.long);
  const extendedSlides = [...slides, ...slides];
  const isDesktop = useIsDesktop();

  useResizeObserver(document.documentElement, () => {
    setOffset(startSlide * totalWidth);
  });

  useEffect(() => {
    setOffset(startSlide * totalWidth);
  }, [startSlide, totalWidth]);

  // buttons controller
  const handleScroll = (direction: 1 | -1 | 0) => {
    setOffset((prevOffset) => prevOffset + direction * totalWidth);
    setDirection(direction);
  };

  // drag controllers
  const handleDrag = (_e: unknown, info: PanInfo) => {
    setOffset((prevOffset) => prevOffset - info.delta.x * dragSensitivity);
    const dir = (-info.delta.x / Math.abs(info.delta.x)) as 1 | -1;
    setDirection(dir);
  };

  const handleDragStart = () => {
    setAnimationType({
      ...animationCurves.easeOut,
    });
  };

  const handleDragEnd = () => {
    const closestSlide = Math.round(offset / totalWidth);
    setOffset(closestSlide * totalWidth);
  };

  return (
    <div className="relative overflow-x-clip flex flex-col justify-center h-screen">
      <div className="relative">
        <motion.div
          className="cursor-grab active:cursor-grabbing flex justify-center items-center"
          style={{
            minHeight: isDesktop ? slideHeight + 54 : slideHeight + 24,
          }}
          drag="x"
          onDrag={handleDrag}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          dragElastic={0}
          dragConstraints={{ left: 0, right: 0 }}
        >
          {/* Slides */}
          {extendedSlides.map((slide, index) => {
            return (
              <CarouselItem
                key={`curve-carousel-item-${index}`}
                index={index}
                slideWidth={slideWidth}
                slideHeight={slideHeight}
                gap={gap}
                offset={offset}
                slide={slide}
                totalSlides={extendedSlides.length}
                direction={direction}
                animationType={animationType as AnimationOptions}
                setAnimationType={setAnimationType}
              />
            );
          })}
        </motion.div>
        {!!ctaLink && (
          <div className="hidden dsk:flex w-full justify-center dsk:flex-grow items-center pt-4 dsk:pt-20">
            <Button variant="light-on-blur" size="large">
              <Link link={ctaLink}>{ctaLink.text}</Link>
            </Button>
          </div>
        )}
      </div>

      {/* Carousel buttons */}
      <div className="flex w-full justify-center gap-2">
        <ArrowBtn
          onClick={() => handleScroll(-1)}
          className={cx(
            "relative rotate-180 size-12 dsk:absolute dsk:top-[calc(50%-8px)] dsk:size-10"
          )}
          style={{
            left: isDesktop
              ? `calc(50% - ${slideWidth / 2 + controlsOffset}px)`
              : "",
          }}
          ariaLabel={accessibilityControlLabelPrevious}
        />
        <ArrowBtn
          onClick={() => handleScroll(1)}
          className="relative size-12 dsk:absolute dsk:top-[calc(50%-8px)] dsk:size-10"
          style={{
            right: isDesktop
              ? `calc(50% - ${slideWidth / 2 + controlsOffset}px)`
              : "",
          }}
          ariaLabel={accessibilityControlLabelNext}
        />
      </div>
      {!!ctaLink && (
        <div className="flex dsk:hidden w-full justify-center dsk:flex-grow items-center pt-4 dsk:pt-20">
          <Button variant="light-on-blur" size="large">
            <Link link={ctaLink}>{ctaLink.text}</Link>
          </Button>
        </div>
      )}
    </div>
  );
};

export default Carousel;
